import { useLazyQuery } from '@apollo/client';
import merge from 'lodash/merge';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { batch, useDispatch, useSelector } from 'react-redux';

import { useCreateAndScheduleCampaignAlertsMutation } from 'modules/campaigns/editor/hooks/useCreateAndScheduleCampaignAlertsMutation';
import { AlertTakeover } from 'modules/campaigns/lib/takeovers';
import { ExperimentTakeover } from 'modules/experiments/takeover';
import { ExperimentTakeoverSource } from 'modules/experiments/takeover/constants';
import { UpdateMediumFieldsType } from 'modules/templateEditor/common';
import {
  TemplateTakeoverContextProvider,
  useTemplateTakeover,
} from 'modules/templateEditor/integrations/InStudioTemplateEditing/context/TemplateTakeoverContext';
import { addNegativeToast } from 'modules/toastContainer';
import { NodeData, NodeId, SendNodeData } from 'modules/workflows';
import { NODE_EDITOR_TYPE, NodeEditor } from 'modules/workflows/components/NodeEditor';
import { useJourneyReviewContext } from 'modules/workflows/context/JourneyReviewContext';
import {
  closeNode,
  deselectNode,
  getIsJourneyReviewPanelEnabled,
  getWorkflowType,
  openNodeRequest,
  reassignNodeId,
  removeClonedJourneyErrorNode,
  removeNodeError,
  updateNodeData,
  upsertWorkflowCampaign,
} from 'modules/workflows/ducks';
import { LocalesContextProvider } from 'utils/contexts/locales/localeSettings';
import noop from 'utils/noop';
import { CampaignStateDefinition } from './BaseSendEditor.constants';
import {
  CampaignAlertJourneyProvider,
  CampaignContextProvider,
  CampaignFormProvider,
  FeatureFlagContextProvider,
  useCampaignAlertJourneyContext,
} from './BaseSendEditor.context';
import {
  fetchCampaignTemplateId,
  fetchExperimentsForCampaignIdQuery,
  validateMultiContent,
} from './BaseSendEditor.graphql';
import { useCampaign, useFeatureFlags, useUpdateEmbeddedEditor } from './BaseSendEditor.hooks';
import { BaseSendEditorProps, CurrentProject, TemplateErrors } from './BaseSendEditor.types';
import {
  getBaseSendEditorSectionConfig,
  isCampaignSTOEnabled,
  isRateLimitValid,
  isSTOValid,
  parseCampaignForSaving,
} from './BaseSendEditor.utils';
import { CampaignNameSection } from './CampaignNameSection';
import { ContentSection } from './ContentSection';
import { ConversionSection } from './ConversionSection';
import { DeliverySection } from './DeliverySection';
import { DisplaySection } from './DisplaySection';
import { ExperimentSection } from './ExperimentSection';
import { NBASection } from './NBASection';
import { EmptyPlacementSection, PlacementsSection } from './PlacementsSection';
import { SendBanner } from './SendBanner';
import { TemplateTakeover } from './TemplateTakeover';
import { UnarchiveBanner } from './UnarchiveBanner';

const BaseSendEditor = (props: BaseSendEditorProps) => {
  const {
    messageMedium,
    mobileApps = [],
    node,
    nodeData = {},
    nodeId,
    nodeType,
    subtitle,
    onUpdate,
    resources = {},
    onTemplateIdUpdate = noop,
    onTemplateUpdate = noop,
    onDisplayPriorityOpen = noop,
    hasPlacements = false,
    ...rest
  } = props;
  const dispatch = useDispatch();
  const {
    isTemplateValidationEnabled,
    studioTemplateEditingIsEnabled,
    isJourneysCampaignPerformanceAlertsEnabled,
    isCampaignAlertOverridesEnabled,
    isEmbeddedMessagingJourneysEnabled,
    isNBAJourneysEnabled,
    isWhatsAppGAEnabled,
  } = useFeatureFlags();

  const {
    campaign,
    errors: campaignErrors,
    cloneErrors,
    isDirty,
    updateIsDirty,
    isReadOnly,
    isCloneWarningBannerShown,
    templateErrors,
    setTemplateErrors,
    updateCampaign,
    placementName,
  } = useCampaign();
  const { getValues, trigger } = useFormContext();

  /**
   * currentLocale holds the current locale value which can be accessed from the node editor preview and template takeover.
   * NOTE: Currently the default currentLocale value is always undefined instead of the template's default value.
   * */

  const isJourneyReviewPanelEnabled = useSelector(getIsJourneyReviewPanelEnabled);
  const { isPublished } = useSelector(getWorkflowType);

  const [suggestedContent, setSuggestedContent] = useState<UpdateMediumFieldsType | undefined>();
  const [currentLocale, setCurrentLocale] = useState<string>();
  const [newNodeId, setNewNodeId] = useState<string>();
  const [templateId, setTemplateId] = useState((nodeData as Partial<SendNodeData>)?.templateId);
  const takeover = useTemplateTakeover();
  const { completeIssue } = useJourneyReviewContext();

  /**
   * Alert takeover
   */
  const { campaignData, isAlertTakeoverOpen, onTakeoverOpen, refetchCampaignAlerts } =
    useCampaignAlertJourneyContext();
  const { createAndScheduleCampaignAlerts } = useCreateAndScheduleCampaignAlertsMutation();

  const { currentProject } = resources;
  const { campaignId, templateId: initialTemplateId } = nodeData as Partial<SendNodeData>;

  const hasTemplateWithoutCampaign = !!initialTemplateId && !!templateId && !campaignId;
  const hasCampaignErrors = Object.values(campaignErrors).some((err) => err.hasError === true);
  const isCampaignRunning = campaign?.campaignState === CampaignStateDefinition.Running;

  /**
   * Display priority takeover for Embedded
   */
  const { shouldConfirmDefaultDisplayPriority, openDefaultDisplayPriorityModal } =
    useUpdateEmbeddedEditor(props);

  /**
   * Determines which sections to show
   */
  const config = useMemo(
    () =>
      getBaseSendEditorSectionConfig({
        campaign,
        campaignId,
        messageMedium,
        hasPlacements,
        isWhatsAppGAEnabled,
      }),
    [campaign, campaignId, messageMedium, hasPlacements, isWhatsAppGAEnabled],
  );

  const updateTemplateId = (id?: number) => {
    if (id !== templateId) {
      onTemplateIdUpdate(id);
      setTemplateId(id);
    }
  };

  /**
   * Gets all data needed to save the node
   */
  const getDataForSaving = () => {
    const nodeCampaign = parseCampaignForSaving(
      campaign,
      currentProject as CurrentProject,
      isEmbeddedMessagingJourneysEnabled,
    );
    const updatedNodeData = {
      ...node.nodeData,
      ...nodeData, // keep existing node data
      ...(!!campaignId && { campaignId }),
      ...(!!templateId && { templateId }),
      resource: { name: nodeCampaign.name, campaign: nodeCampaign },
    };

    return {
      campaign: nodeCampaign,
      messageMedium,
      node: { ...node, nodeData: updatedNodeData },
      nodeClass: props.deferredNodeClass,
      workflow: props.workflowData,
    };
  };

  const updateExternalData = (
    updatedNodeData: Partial<SendNodeData>,
    updatedNodeId: string,
    opts?: any,
  ) =>
    typeof onUpdate === 'function'
      ? onUpdate(updatedNodeData, updatedNodeId)
      : dispatch(updateNodeData(updatedNodeData as NodeData, updatedNodeId, opts));

  /**
   * Data needed for the experiment section and takeover
   */
  const [
    fetchExperimentFromCampaign,
    {
      loading: experimentDataIsLoading,
      data: {
        campaign: { experiments = null },
      } = { campaign: {} },
    },
  ] = useLazyQuery(fetchExperimentsForCampaignIdQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { campaignId },
  });
  /**
   * The campaign name is not automatically assigned within the campaign context
   * when a campaign is created. Users have the flexibility to override the default
   * name assigned to it. This fallback mechanism ensures that the experiment name
   * can incorporate the default campaign name if the user hasn't specified a campaign
   * name at the point of experiment creation.
   */
  const campaignName = campaign?.name || (nodeData as Partial<SendNodeData>)?.name || '';

  useEffect(() => {
    if (experiments === null && campaignId) {
      fetchExperimentFromCampaign();
    }
  }, [campaignId]);

  /**
   * SAVE functionality
   */

  const checkIsFormValid = () => {
    if (!isSTOValid(campaign, campaignErrors)) return false;
    if (!isRateLimitValid(campaign, campaignErrors)) return false;
    if (campaignErrors.SingleAttributionPeriod.hasError) return false;
    if (campaignErrors.MultiAttributionPeriod.hasError) return false;
    return true;
  };

  const handleUpdateFinish = (returnedNodeData: SendNodeData, returnedNodeId: NodeId) =>
    batch(() => {
      updateIsDirty(false);
      const isNewNode = Number(nodeId) < 0;
      dispatch(closeNode());

      if (isNewNode) {
        dispatch(reassignNodeId(nodeId as NodeId, returnedNodeId));
      }
      dispatch(deselectNode(isNewNode ? returnedNodeId : (nodeId as NodeId)));

      updateExternalData(returnedNodeData, returnedNodeId);

      const campaignAlertsEnabled = returnedNodeData?.resource?.campaign?.campaignAlertsEnabled;
      const canCreateAndScheduleCampaignAlerts =
        isJourneysCampaignPerformanceAlertsEnabled &&
        isCampaignRunning &&
        campaignId &&
        campaignAlertsEnabled;

      if (canCreateAndScheduleCampaignAlerts) {
        createAndScheduleCampaignAlerts({
          variables: { campaignId },
          onCompleted: () => {
            refetchCampaignAlerts();
          },
        });
      }
    });

  const handleUpdate = (requiresValidation = true) => {
    const args = { ...getDataForSaving(), onFinish: handleUpdateFinish };
    if (!checkIsFormValid() && requiresValidation) {
      dispatch(addNegativeToast('Incorrect setup.'));
    } else if (shouldConfirmDefaultDisplayPriority) {
      openDefaultDisplayPriorityModal({
        onConfirm: () => dispatch(upsertWorkflowCampaign(args)),
      });
    } else {
      dispatch(upsertWorkflowCampaign(args));
    }
  };

  /**
   * TEMPLATE functionality
   */

  const [fetchTemplateIdFromCampaign] = useLazyQuery(fetchCampaignTemplateId, {
    fetchPolicy: 'cache-and-network',
    variables: { campaignId },
    onCompleted: (resp) => updateTemplateId(resp?.campaign?.templateId),
  });

  const removeErrorHighlight = (errors: TemplateErrors) => {
    // only remove highlighting if other errors are fixed
    if (errors.length === 0 && !cloneErrors?.conversions && !cloneErrors?.customConversion) {
      if (isJourneyReviewPanelEnabled) {
        completeIssue(nodeId as string);
      } else {
        dispatch(removeNodeError());
      }

      if (nodeId) dispatch(removeClonedJourneyErrorNode(nodeId));
    }
  };

  const [validateTemplates] = useLazyQuery(validateMultiContent, {
    fetchPolicy: 'cache-and-network',
    variables: { templateIds: [templateId] },
    onCompleted: (data) => {
      const errors = data?.validateMultiContent?.errors[0]?.value;
      setTemplateErrors(errors, removeErrorHighlight);
    },
  });

  const handleTemplateUpdate = (id?: string) => {
    if (id && id !== nodeId) {
      dispatch(reassignNodeId(nodeId as string, id));
      dispatch(openNodeRequest(id, { childNodeData: { type: nodeType } }));
    } else if (node?.hasError) {
      validateTemplates();
    }
    onTemplateUpdate();
  };

  const handleSaveTemplateFinish = (nextNodeData: SendNodeData, nextNodeId: string) => {
    updateTemplateId(nextNodeData.templateId);
    updateIsDirty(false);
    updateExternalData(nextNodeData, nodeId as string, { closeEditor: false });

    if (takeover.isOpen) {
      if (nextNodeId !== nodeId) {
        setNewNodeId(nextNodeId);
      }

      takeover.setView(takeover.viewOptions.settings);
    } else {
      handleTemplateUpdate(nextNodeId);
    }
  };

  const saveCampaignWithNewTemplate = (
    selectedTemplateId?: number,
    onFailure: () => void = noop,
  ) => {
    const { node: updatedNode, ...data } = getDataForSaving();
    const args = {
      ...data,
      node: merge({}, updatedNode, {
        nodeData: { templateId: selectedTemplateId },
      }),
      onFinish: handleSaveTemplateFinish,
      onFailure,
    };

    dispatch(upsertWorkflowCampaign(args));
  };

  const handleAttachTemplate = (id?: number) => {
    if (studioTemplateEditingIsEnabled) {
      /**
       * Unfortunately, this needs to be saved at this point so that we do not
       * edit the base template.
       * See https://iterable.atlassian.net/browse/WF-4018 for details.
       */
      saveCampaignWithNewTemplate(id);
    } else {
      // updates the template id and closes the takeover
      if (id && id !== templateId) {
        updateTemplateId(id);
        updateIsDirty(true);
      }
      takeover.close();
    }
  };

  const handleBack = () => {
    updateCampaign({ placementId: undefined });
  };

  const handleContinue = () => {
    const { placementId } = getValues();
    if (!placementId) {
      trigger();
    } else {
      updateCampaign({ placementId: Number(placementId) });
    }
  };

  useEffect(() => {
    /**
     * This is needed to fetch the updated templateId from a newly created
     * campaign.  EG: when a template is added to a new send node.
     */
    if (campaignId) {
      fetchTemplateIdFromCampaign();
    }
  }, [campaignId]);

  useEffect(() => {
    if (node?.hasError && templateId) validateTemplates();
  }, [node, templateId]);

  useEffect(() => {
    /**
     * When a user clones a tile with a template ID attached,
     * we need to create a new campaign and template ID
     */
    if (hasTemplateWithoutCampaign) {
      saveCampaignWithNewTemplate(initialTemplateId, () => updateTemplateId());
    }
  }, []);

  useEffect(() => {
    /**
     * We need to validate the templates each time the user closes the takeover
     * To see if they have fixed all the errors or if they have created any new ones
     *
     */
    if (!takeover.isOpen) {
      validateTemplates();
    }
  }, [takeover.isOpen]);

  return (
    <>
      <NodeEditor
        {...rest}
        node={node}
        dataTest={`send-${messageMedium.toLowerCase()}-editor`}
        editorType={NODE_EDITOR_TYPE.CAMPAIGN}
        onUpdate={handleUpdate}
        canUpdate={!hasCampaignErrors}
        isLoadingIconShown={hasTemplateWithoutCampaign || props.isLoading}
        isCloneWarningBannerShown={isCloneWarningBannerShown}
        isFooterShown={!config.showAddPlacementsSection}
        /** These are used to check if the discard changes modal should be shown */
        /** Don't show the modal if user is on Embedded initial placement step */
        getHasUnsavedChanges={() => isDirty && !config.showPlacementSection}
        onBack={config.showBackButton ? handleBack : undefined}
        onContinue={config.showPlacementSection ? handleContinue : undefined}
      >
        {isCampaignRunning && (
          <SendBanner>
            This campaign is currently running. If you edit it, users will immediately start
            receiving the new version of the campaign.
          </SendBanner>
        )}
        {campaign?.campaignState === CampaignStateDefinition.Archived && (
          <UnarchiveBanner campaignId={campaignId} />
        )}
        {isNBAJourneysEnabled && campaignId && isPublished && (
          <NBASection
            messageMedium={messageMedium}
            onPreviewClick={(content) => {
              setSuggestedContent(content);
            }}
          />
        )}
        {config.showNameInput && (
          <CampaignNameSection campaignId={campaignId} subtitle={subtitle || placementName} />
        )}
        {config.showPlacementSection && <PlacementsSection />}
        {config.showAddPlacementsSection && <EmptyPlacementSection />}
        {config.showContentSection && (
          <ContentSection
            {...props}
            title="Content"
            banner={props.contentBanner}
            templateId={templateId}
            campaignId={campaignId}
            showTemplateErrorsWarning={
              !isCloneWarningBannerShown && !!templateErrors.length && isTemplateValidationEnabled
            }
            showSnippetErrorsWarning={isCloneWarningBannerShown && !!templateErrors.length}
            // EXTERNAL editing only (ie: old, non-aurora, non-inline-edit preview)
            onRefreshClick={fetchTemplateIdFromCampaign}
            isDisabled={isReadOnly}
            currentLocale={currentLocale}
            setCurrentLocale={setCurrentLocale}
          />
        )}
        {config.showDisplaySection && (
          <DisplaySection
            campaignId={campaignId}
            title="Display"
            banner={props.deliveryBanner}
            isDisabled={isReadOnly}
            onDisplayPriorityOpen={onDisplayPriorityOpen}
          />
        )}
        {config.showDeliverySection && (
          <DeliverySection
            {...props}
            title="Delivery"
            banner={props.deliveryBanner}
            messageMedium={messageMedium}
            project={currentProject as CurrentProject}
            mobileApps={mobileApps}
            isDisabled={isReadOnly}
            templateId={templateId}
          />
        )}
        {config.showConversionSection && (
          <ConversionSection
            {...props}
            title={'Conversions'}
            banner={props.conversionsBanner}
            isDisabled={isReadOnly}
          />
        )}
        {config.showExperimentSection && (
          <ExperimentSection
            title="Experiment"
            campaignId={campaignId}
            campaignName={campaignName}
            campaignState={campaign?.campaignState}
            experiments={experiments}
            isCampaignSTOEnabled={isCampaignSTOEnabled(campaign)}
            isLoading={experimentDataIsLoading}
            isDisabled={isReadOnly}
            onRefreshClick={fetchExperimentFromCampaign}
          />
        )}
      </NodeEditor>
      {/**
       * This is put outside of `<NodeEditor />` so that it can fully take over
       * the page without being restricted by the z-index on `<NodeEditor />`
       */}
      <ExperimentTakeover
        source={ExperimentTakeoverSource.WORKFLOW_NODE_EDITOR}
        currentLocale={currentLocale}
        setCurrentLocale={setCurrentLocale}
        onClose={() => fetchExperimentFromCampaign()}
      />
      <TemplateTakeover
        messageMedium={messageMedium}
        campaignId={campaignId}
        templateId={templateId}
        onAttachTemplate={handleAttachTemplate}
        onClose={() => {
          if (suggestedContent) setSuggestedContent(undefined);
          handleTemplateUpdate(newNodeId);
        }}
        currentLocale={currentLocale}
        setCurrentLocale={setCurrentLocale}
        placementId={campaign?.placementId}
        suggestedContent={suggestedContent}
      />
      {isJourneysCampaignPerformanceAlertsEnabled &&
        isCampaignAlertOverridesEnabled &&
        isAlertTakeoverOpen && (
          <AlertTakeover campaignData={campaignData} onTakeoverOpen={onTakeoverOpen} />
        )}
    </>
  );
};

export default (props: BaseSendEditorProps) => (
  <FeatureFlagContextProvider {...props}>
    <LocalesContextProvider>
      <CampaignFormProvider {...props}>
        <CampaignContextProvider {...props}>
          <TemplateTakeoverContextProvider>
            <CampaignAlertJourneyProvider messageMedium={props.messageMedium}>
              <BaseSendEditor {...props} />
            </CampaignAlertJourneyProvider>
          </TemplateTakeoverContextProvider>
        </CampaignContextProvider>
      </CampaignFormProvider>
    </LocalesContextProvider>
  </FeatureFlagContextProvider>
);
